// ─────────────────────────────────────────────────────────────────────────────
// Logo Cari sayfasi — ticket sayfasi gibi: ust grid + alt detay pane (Splitter).
// Grid: modulun kendi kopyasi olan CustomDataGrid (components/CustomDataGrid).
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useState } from 'react';
import Splitter, { Item as SplitterItem } from 'devextreme-react/splitter';
import CheckBox from 'devextreme-react/check-box';
import notify from 'devextreme/ui/notify';
import { CustomDataGrid } from '../components/CustomDataGrid';
import { Customer } from '../types';
import { getCustomers } from '../api/customers';
import { CustomerDetailPane } from './CustomerDetailPane';
import { lt } from '../i18n';

const CARD_TYPE_TEXT: Record<number, string> = { 0: 'Alici+Satici', 3: 'Alici', 4: 'Satici' };

export default function LogoCustomersPage() {
  const [rows, setRows] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [onlyActive, setOnlyActive] = useState(true);
  const [selected, setSelected] = useState<Customer | null>(null);
  // Ticket sayfasi gibi altta giris bolumu hep hazir dursun.
  const [creating, setCreating] = useState(true);

  // Splitter daralt/genislet (ticket sayfasi gibi ok tuslari)
  const [gridKey, setGridKey] = useState(0);
  const [topCollapsed, setTopCollapsed] = useState(false);     // ust: grid
  const [bottomCollapsed, setBottomCollapsed] = useState(false); // alt: detay
  const toggleBottom = useCallback(() => {
    setBottomCollapsed((p) => !p); setTopCollapsed(false);
    setTimeout(() => setGridKey((k) => k + 1), 250);
  }, []);
  const toggleTop = useCallback(() => {
    setTopCollapsed((p) => !p); setBottomCollapsed(false);
    setTimeout(() => setGridKey((k) => k + 1), 250);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await getCustomers({ onlyActive });
    setLoading(false);
    if (res.ok) {
      setRows(res.data ?? []);
    } else {
      notify(`${lt('loadError')}: ${res.message ?? ''}`, 'error', 4000);
      setRows([]);
    }
  }, [onlyActive]);

  useEffect(() => { load(); }, [load]);

  const openRow = useCallback((c: Customer) => { setCreating(false); setSelected(c); }, []);
  const startNew = useCallback(() => { setSelected(null); setCreating(true); }, []);

  const handleSaved = useCallback((saved: Customer) => {
    setCreating(false);
    setSelected(saved);
    load();
  }, [load]);

  const columns = useMemo(() => [
    { dataField: 'code', caption: lt('code'), width: 120 },
    { dataField: 'title', caption: lt('title'), width: 'auto' as const },
    {
      dataField: 'cardType', caption: lt('cardType'), width: 120,
      cellRender: (d: any) => CARD_TYPE_TEXT[d.data.cardType ?? -1] ?? d.data.cardType ?? '—',
    },
    { dataField: 'city', caption: lt('city'), width: 120 },
    { dataField: 'phone1', caption: lt('phone'), width: 130 },
    { dataField: 'email', caption: lt('email'), width: 180 },
    { dataField: 'vatId', caption: lt('vatId'), width: 120 },
    {
      dataField: 'isActive', caption: lt('status'), width: 90, alignment: 'center' as const,
      cellRender: (d: any) => (d.data.isActive ? lt('active') : lt('passive')),
    },
  ], []);

  // onlyActive filtresi + splitter daralt oku araç cubuğunda.
  const toolbarItems = useMemo(() => [
    {
      location: 'before' as const,
      render: () => (
        <CheckBox value={onlyActive} text={lt('onlyActive')} onValueChanged={(e: any) => setOnlyActive(!!e.value)} />
      ),
    },
    {
      location: 'after' as const,
      render: () => (
        <button type="button" className="splitter-collapse-btn" title="Alt paneli ac/kapat" onClick={toggleBottom}>
          <i className={bottomCollapsed ? 'dx-icon-chevronup' : 'dx-icon-chevrondown'} />
        </button>
      ),
    },
  ], [onlyActive, bottomCollapsed, toggleBottom]);

  const showDetail = creating || !!selected;

  return (
    <div className="logo-customers-page" style={{ height: 'calc(100vh - 120px)' }}>
      <Splitter orientation="vertical" style={{ height: '100%' }}>
        <SplitterItem size="60%" minSize="120px" collapsible collapsed={topCollapsed}>
          <CustomDataGrid
            key={gridKey}
            data={rows}
            columns={columns}
            keyExpr="code"
            height="100%"
            t={lt}
            selectionEnabled={false}
            filterRowVisible
            noDataText={loading ? '...' : lt('loadError')}
            onRowClick={(data: Customer) => openRow(data)}
            onAddNew={startNew}
            onRefresh={load}
            additionalToolbarItems={toolbarItems}
          />
        </SplitterItem>

        <SplitterItem size="40%" minSize="120px" collapsible collapsed={bottomCollapsed}>
          {showDetail ? (
            <CustomerDetailPane
              customer={creating ? null : selected}
              topCollapsed={topCollapsed}
              onToggleTop={toggleTop}
              onSaved={handleSaved}
              onCancel={() => { setSelected(null); setCreating(true); }}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
              {lt('selectCustomer')}
            </div>
          )}
        </SplitterItem>
      </Splitter>
    </div>
  );
}
